/** Report chart section: line / bar / dual_axis / donut / bubble / funnel (Recharts, dark PlayAd styling). */
import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import { cn } from "@/lib/cn";
import { formatValue, normalizeFormat, toNum } from "./fmt";

export interface ChartSpec {
  title: string;
  chart: "line" | "bar" | "dual_axis" | "donut" | "bubble" | "funnel";
  series: { key: string; label: string; axis?: "left" | "right" }[];
  data: Record<string, unknown>[];
}

export const CHART_COLORS = ["#5643e1", "#3faa60", "#8b7ff0", "#e3b341", "#6cb6c9", "#e5484d", "#be6a3f"];

const X_CANDIDATES = ["date", "day", "period", "week", "label", "name", "x", "stage", "category", "platform", "campaign"];

function inferXKey(data: Record<string, unknown>[], seriesKeys: string[]): string {
  const first = data[0] ?? {};
  const keys = Object.keys(first).filter((k) => !seriesKeys.includes(k));
  return X_CANDIDATES.find((c) => keys.includes(c)) ?? keys.find((k) => typeof first[k] === "string") ?? keys[0] ?? "x";
}

const tooltipStyle = {
  contentStyle: { background: "#1d1d1f", border: "1px solid #3a3a3e", borderRadius: 10, fontSize: 12, color: "#f4f4f5", boxShadow: "0 12px 40px -8px rgba(0,0,0,0.6)" },
  labelStyle: { color: "#a1a1a6", marginBottom: 4 },
  itemStyle: { padding: 0 },
};

function tickFor(key: string) {
  const fmt = normalizeFormat(undefined, key);
  return (v: unknown) => {
    const n = toNum(v);
    if (n === null) return String(v ?? "");
    if (fmt === "currency") return Math.abs(n) >= 1000 ? `$${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}K` : `$${Math.round(n)}`;
    if (fmt === "percent") return formatValue(n, "percent");
    return Math.abs(n) >= 1000 ? `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}K` : String(Math.round(n * 100) / 100);
  };
}

function xTick(v: unknown): string {
  const s = String(v ?? "");
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : s.length > 14 ? `${s.slice(0, 13)}…` : s;
}

export function ReportChart({ spec, animate = true, height = 260 }: { spec: ChartSpec; animate?: boolean; height?: number }) {
  const seriesKeys = spec.series.map((s) => s.key);
  const xKey = useMemo(() => inferXKey(spec.data, seriesKeys), [spec.data, seriesKeys.join("|")]);
  const data = useMemo(() => spec.data.map((d) => ({ ...d, ...Object.fromEntries(seriesKeys.map((k) => [k, toNum(d[k])])) })), [spec.data, seriesKeys.join("|")]);
  const fmtTip = (value: unknown, name: unknown) => {
    const s = spec.series.find((x) => x.label === name || x.key === name);
    return [formatValue(value, normalizeFormat(undefined, s?.key ?? String(name))), s?.label ?? String(name)] as [string, string];
  };

  if (!spec.data.length || !spec.series.length) return <div className="grid h-40 place-items-center text-[13px] text-muted">No data for this chart.</div>;

  const legend = (
    <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1.5 text-[12.5px] text-fg/85">
      {spec.series.map((s, i) => (
        <span key={s.key} className="inline-flex items-center gap-1.5">
          <span className="size-2 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
          {s.label}
          {spec.chart === "dual_axis" && s.axis === "right" && <span className="text-subtle">(right axis)</span>}
        </span>
      ))}
    </div>
  );

  if (spec.chart === "donut") return <Donut spec={spec} xKey={xKey} animate={animate} />;
  if (spec.chart === "funnel") return <Funnel spec={spec} xKey={xKey} />;

  const grid = <CartesianGrid vertical={false} strokeDasharray="0" />;
  const xAxis = <XAxis dataKey={xKey} tickLine={false} axisLine={false} tickFormatter={xTick} minTickGap={24} tickMargin={8} />;

  if (spec.chart === "bubble") {
    const [sx, sy, sz] = spec.series;
    return (
      <div>
        <div className="mb-3 text-[12.5px] text-muted">
          {sx?.label} × {sy?.label}
          {sz ? ` · size: ${sz.label}` : ""}
        </div>
        <ResponsiveContainer width="100%" height={height}>
          <ScatterChart margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
            {grid}
            <XAxis type="number" dataKey={sx?.key} name={sx?.label} tickLine={false} axisLine={false} tickFormatter={tickFor(sx?.key ?? "")} />
            <YAxis type="number" dataKey={sy?.key} name={sy?.label} tickLine={false} axisLine={false} width={52} tickFormatter={tickFor(sy?.key ?? "")} />
            {sz && <ZAxis type="number" dataKey={sz.key} range={[60, 900]} name={sz.label} />}
            <Tooltip {...tooltipStyle} cursor={{ strokeDasharray: "3 3" }} formatter={fmtTip} labelFormatter={() => ""} />
            <Scatter data={data} fill={CHART_COLORS[0]} fillOpacity={0.7} isAnimationActive={animate} />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (spec.chart === "bar") {
    return (
      <div>
        {legend}
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            {grid}
            {xAxis}
            <YAxis tickLine={false} axisLine={false} width={52} tickFormatter={tickFor(spec.series[0]!.key)} />
            <Tooltip {...tooltipStyle} formatter={fmtTip} />
            {spec.series.map((s, i) => (
              <Bar key={s.key} dataKey={s.key} name={s.label} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} maxBarSize={36} isAnimationActive={animate} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (spec.chart === "dual_axis") {
    const left = spec.series.filter((s) => s.axis !== "right");
    const right = spec.series.filter((s) => s.axis === "right");
    return (
      <div>
        {legend}
        <ResponsiveContainer width="100%" height={height}>
          <ComposedChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            {grid}
            {xAxis}
            <YAxis yAxisId="left" tickLine={false} axisLine={false} width={52} tickFormatter={tickFor(left[0]?.key ?? "")} />
            <YAxis
              yAxisId="right"
              orientation="right"
              tickLine={false}
              axisLine={false}
              width={52}
              tickFormatter={tickFor(right[0]?.key ?? "")}
              label={right[0] ? { value: right[0].label.length > 18 ? "" : right[0].label, angle: 90, position: "insideRight", fill: "#8a8a90", fontSize: 11 } : undefined}
            />
            <Tooltip {...tooltipStyle} formatter={fmtTip} />
            {spec.series.map((s, i) => (
              <Line key={s.key} yAxisId={s.axis === "right" ? "right" : "left"} type="monotone" dataKey={s.key} name={s.label} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2.2} dot={false} activeDot={{ r: 4 }} isAnimationActive={animate} />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div>
      {legend}
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          {grid}
          {xAxis}
          <YAxis tickLine={false} axisLine={false} width={52} tickFormatter={tickFor(spec.series[0]!.key)} />
          <Tooltip {...tooltipStyle} formatter={fmtTip} />
          {spec.series.map((s, i) => (
            <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2.2} dot={false} activeDot={{ r: 4 }} isAnimationActive={animate} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function Donut({ spec, xKey, animate }: { spec: ChartSpec; xKey: string; animate: boolean }) {
  const s = spec.series[0]!;
  const rows = spec.data.map((d) => ({ name: String(d[xKey] ?? "—"), value: toNum(d[s.key]) ?? 0 })).filter((r) => r.value > 0);
  const total = rows.reduce((a, r) => a + r.value, 0) || 1;
  const fmt = normalizeFormat(undefined, s.key);
  return (
    <div className="flex flex-col items-center gap-5 sm:flex-row">
      <div className="relative size-[200px] shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={rows} dataKey="value" nameKey="name" innerRadius="62%" outerRadius="95%" paddingAngle={1.5} stroke="none" isAnimationActive={animate}>
              {rows.map((_, i) => (
                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip {...tooltipStyle} formatter={(v: unknown, n: unknown) => [formatValue(v, fmt), String(n)]} />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 grid place-items-center text-center">
          <div>
            <div className="text-[18px] font-semibold tnum">{formatValue(total, fmt)}</div>
            <div className="text-[11px] text-muted">{s.label}</div>
          </div>
        </div>
      </div>
      <ul className="w-full min-w-0 space-y-2 text-[13px]">
        {rows.map((r, i) => (
          <li key={r.name} className="flex items-center gap-2">
            <span className="size-2.5 shrink-0 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
            <span className="min-w-0 flex-1 truncate text-fg/85">{r.name}</span>
            <span className="font-medium tnum">{formatValue(r.value, fmt)}</span>
            <span className="w-12 text-right text-muted tnum">{((r.value / total) * 100).toFixed(0)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Funnel({ spec, xKey }: { spec: ChartSpec; xKey: string }) {
  const s = spec.series[0]!;
  const rows = spec.data.map((d) => ({ name: String(d[xKey] ?? "—"), value: toNum(d[s.key]) ?? 0 }));
  const max = Math.max(1, ...rows.map((r) => r.value));
  const fmt = normalizeFormat(undefined, s.key);
  return (
    <ol className="space-y-1.5">
      {rows.map((r, i) => {
        const prev = rows[i - 1];
        const drop = prev && prev.value > 0 ? 1 - r.value / prev.value : null;
        return (
          <li key={`${r.name}-${i}`}>
            {drop !== null && (
              <div className="flex items-center gap-2 py-0.5 pl-2 text-[11.5px] text-muted">
                <span className="size-1.5 rounded-full bg-danger" />
                Drop-off {(drop * 100).toFixed(1)}%
              </div>
            )}
            <div className="flex items-center gap-3">
              <span className="w-28 shrink-0 truncate text-[12.5px] text-fg/85 sm:w-36">{r.name}</span>
              <div className="h-8 min-w-0 flex-1 rounded-md bg-elevated/50">
                <div className={cn("flex h-full items-center rounded-md px-2.5 text-[12px] font-semibold text-white tnum")} style={{ width: `${Math.max(4, (r.value / max) * 100)}%`, background: CHART_COLORS[0], opacity: 1 - i * 0.12 }}>
                  {formatValue(r.value, fmt)}
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
